import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  Button,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import AddIcon from '@mui/icons-material/Add';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import CustomButton from '../../components/mui/CustomButton';
import ConfirmationModal from '../../components/ConfirmationModal';
import ExperienceFormModal from './ExperienceFormModal';
import ExperienceDetailsModal from './ExperienceDetailsModal';
import {
  deleteExperience,
  getAdminExperiences,
  getPublicExperiences,
  toggleExperienceStatus,
} from '../../actions/experienceAuth';

const CATEGORIES = [
  'All Categories',
  'Adventure',
  'Dining & Culinary',
  'Wellness & Spa',
  'Travel & Getaways',
  'Culture & Heritage',
  'Workshops & Learning',
  'Water Sports',
  'Outdoor & Nature',
  'Luxury Experiences',
  'Nightlife & Events',
];

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '24px',
      border: '1px solid',
      borderColor: alpha('#11409B', 0.08),
      bgcolor: '#fff',
      boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    '& fieldset': { borderColor: alpha('#11409B', 0.2) },
    '&:hover fieldset': { borderColor: '#11409B' },
  },
};

const ExperiencesTab = ({ onStatsUpdate }) => {
  const dispatch = useDispatch();

  const [experiences, setExperiences] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [cityFilter, setCityFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  // Mode: 'table' or 'catalog' (public browse preview)
  const [viewMode, setViewMode] = useState('table');

  // Modals state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedForDetails, setSelectedForDetails] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchExperiences = useCallback(() => {
    setLoading(true);

    const params = {
      page,
      size: pageSize,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(selectedCategory !== 'All Categories' ? { category: selectedCategory } : {}),
      ...(cityFilter.trim() ? { city: cityFilter.trim() } : {}),
      ...(minPrice !== '' ? { minPrice: Number(minPrice) } : {}),
      ...(maxPrice !== '' ? { maxPrice: Number(maxPrice) } : {}),
    };

    if (activeFilter === 'ACTIVE') params.active = true;
    if (activeFilter === 'INACTIVE') params.active = false;

    // In catalog view, call the public endpoint; in table view, call the admin endpoint
    const actionToDispatch =
      viewMode === 'catalog'
        ? getPublicExperiences(params, handleSuccess, handleError)
        : getAdminExperiences(params, handleSuccess, handleError);

    function handleSuccess(res) {
      setLoading(false);
      const data = res?.data?.data || res?.data || [];
      const list = Array.isArray(data)
        ? data
        : data?.content || data?.experiences || data?.items || [];
      const total =
        res?.data?.totalElements ??
        res?.data?.total ??
        res?.data?.results ??
        data?.totalElements ??
        list.length;

      setExperiences(list);
      setTotalCount(total);

      if (onStatsUpdate) {
        const activeCount = list.filter((x) => x.active ?? x.isActive).length;
        const totalSlots = list.reduce(
          (acc, cur) => acc + (Number(cur.slots || cur.capacity || cur.totalSlots) || 0),
          0
        );
        onStatsUpdate({
          totalExperiences: total,
          activeExperiences: activeCount,
          totalSlots,
        });
      }
    }

    function handleError(err) {
      setLoading(false);
      const msg = err?.response?.data?.message || err?.message || 'Failed to load experiences';
      enqueueSnackbar(msg, { variant: 'error' });
    }

    dispatch(actionToDispatch);
  }, [dispatch, page, pageSize, search, selectedCategory, cityFilter, activeFilter, minPrice, maxPrice, viewMode, onStatsUpdate]);

  useEffect(() => {
    fetchExperiences();
  }, [fetchExperiences]);

  const handleClearFilters = () => {
    setSearch('');
    setSelectedCategory('All Categories');
    setCityFilter('');
    setActiveFilter('ALL');
    setMinPrice('');
    setMaxPrice('');
    setPage(0);
  };

  const handleToggleActive = (experience) => {
    const id = experience.id || experience._id;
    const currentActive = experience.active ?? experience.isActive ?? true;
    const newStatus = !currentActive;

    // Optimistic update
    setExperiences((prev) =>
      prev.map((item) =>
        (item.id || item._id) === id ? { ...item, active: newStatus, isActive: newStatus } : item
      )
    );

    dispatch(
      toggleExperienceStatus(
        id,
        newStatus,
        () => {
          enqueueSnackbar(`Experience ${newStatus ? 'activated' : 'deactivated'} successfully`, {
            variant: 'success',
          });
        },
        (err) => {
          // Revert optimistic update
          setExperiences((prev) =>
            prev.map((item) =>
              (item.id || item._id) === id
                ? { ...item, active: currentActive, isActive: currentActive }
                : item
            )
          );
          const msg = err?.response?.data?.message || 'Failed to update status';
          enqueueSnackbar(msg, { variant: 'error' });
        }
      )
    );
  };

  const handleDeleteConfirm = () => {
    if (!selectedForDelete) return;
    const id = selectedForDelete.id || selectedForDelete._id;
    setDeleteLoading(true);

    dispatch(
      deleteExperience(
        id,
        () => {
          setDeleteLoading(false);
          setDeleteModalOpen(false);
          setSelectedForDelete(null);
          enqueueSnackbar('Experience and associated files deleted successfully', {
            variant: 'success',
          });
          fetchExperiences();
        },
        (err) => {
          setDeleteLoading(false);
          const msg = err?.response?.data?.message || 'Failed to delete experience';
          enqueueSnackbar(msg, { variant: 'error' });
        }
      )
    );
  };

  const handleExportExcel = () => {
    if (!experiences.length) {
      enqueueSnackbar('No experiences to export', { variant: 'warning' });
      return;
    }
    const exportData = experiences.map((item) => ({
      ID: item.id || item._id,
      Title: item.title || item.name,
      Category: item.category,
      Location: item.location || item.venue,
      City: item.city,
      Duration: item.duration,
      'Base Price (INR)': item.pricing ?? item.price,
      'Discount Price (INR)': item.discountPrice ?? item.offerPrice ?? '—',
      'Total Slots': item.slots ?? item.capacity,
      Status: (item.active ?? item.isActive) ? 'Active' : 'Inactive',
      Overview: item.overview || item.description,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Experiences');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), 'experiences_catalog.xlsx');
  };

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  return (
    <Box>
      {/* Action Toolbar */}
      <PanelCard sx={{ mb: 3, p: 2.5 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={3.5}>
            <TextField
              size="small"
              placeholder="Search experiences by title or keywords..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              fullWidth
              sx={fieldSx}
              InputProps={{
                startAdornment: <SearchOutlinedIcon fontSize="small" sx={{ color: '#94a3b8', mr: 1 }} />,
              }}
            />
          </Grid>

          {/* Category Filter */}
          <Grid item xs={6} md={2.5}>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setPage(0);
                }}
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* City / Destination Filter */}
          <Grid item xs={6} md={2}>
            <TextField
              size="small"
              placeholder="Filter by city..."
              value={cityFilter}
              onChange={(e) => {
                setCityFilter(e.target.value);
                setPage(0);
              }}
              fullWidth
              sx={fieldSx}
            />
          </Grid>

          {/* Status Filter */}
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Status</InputLabel>
              <Select
                value={activeFilter}
                label="Status"
                onChange={(e) => {
                  setActiveFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="ACTIVE">Active Only</MenuItem>
                <MenuItem value="INACTIVE">Inactive Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* View Mode Toggle & Add Button */}
          <Grid item xs={6} md={2}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title={viewMode === 'table' ? 'Switch to Public Catalog Grid' : 'Switch to Table'}>
                <IconButton
                  onClick={() => setViewMode((prev) => (prev === 'table' ? 'catalog' : 'table'))}
                  sx={{
                    bgcolor: alpha('#11409B', 0.08),
                    color: '#11409B',
                    borderRadius: '12px',
                    '&:hover': { bgcolor: alpha('#11409B', 0.16) },
                  }}
                >
                  {viewMode === 'table' ? <GridViewOutlinedIcon /> : <TableChartOutlinedIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Refresh">
                <IconButton
                  onClick={fetchExperiences}
                  sx={{
                    bgcolor: alpha('#11409B', 0.08),
                    color: '#11409B',
                    borderRadius: '12px',
                    '&:hover': { bgcolor: alpha('#11409B', 0.16) },
                  }}
                >
                  <RefreshOutlinedIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Export to Excel">
                <IconButton
                  onClick={handleExportExcel}
                  sx={{
                    bgcolor: alpha('#11409B', 0.08),
                    color: '#11409B',
                    borderRadius: '12px',
                    '&:hover': { bgcolor: alpha('#11409B', 0.16) },
                  }}
                >
                  <FileDownloadOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>

          {/* Secondary Filter Row: Price Range & Clear Filters */}
          <Grid item xs={12}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                PRICE RANGE:
              </Typography>
              <TextField
                size="small"
                type="number"
                placeholder="Min ₹"
                value={minPrice}
                onChange={(e) => {
                  setMinPrice(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 110, ...fieldSx }}
              />
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                -
              </Typography>
              <TextField
                size="small"
                type="number"
                placeholder="Max ₹"
                value={maxPrice}
                onChange={(e) => {
                  setMaxPrice(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 110, ...fieldSx }}
              />

              {(search || selectedCategory !== 'All Categories' || cityFilter || activeFilter !== 'ALL' || minPrice || maxPrice) && (
                <Button
                  onClick={handleClearFilters}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#dc2626',
                    ml: 1,
                  }}
                >
                  Reset Filters
                </Button>
              )}

              <Box sx={{ flexGrow: 1 }} />

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedForEdit(null);
                  setFormModalOpen(true);
                }}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 2.5,
                  background: 'linear-gradient(135deg, #0c3483 0%, #11409B 100%)',
                  boxShadow: '0 4px 16px rgba(17, 64, 155, 0.25)',
                }}
              >
                Add Experience
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </PanelCard>

      {/* Main Content: Table or Catalog Cards */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={40} sx={{ color: '#11409B' }} />
        </Box>
      ) : experiences.length === 0 ? (
        <PanelCard sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: '#475569', fontWeight: 700 }}>
            No experiences found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5, mb: 2 }}>
            Try adjusting your search criteria or add a new curated experience.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedForEdit(null);
              setFormModalOpen(true);
            }}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #0c3483 0%, #11409B 100%)',
            }}
          >
            Add First Experience
          </Button>
        </PanelCard>
      ) : viewMode === 'table' ? (
        <PanelCard>
          <TableContainer>
            <Table sx={{ minWidth: 800 }}>
              <TableHead sx={{ bgcolor: alpha('#11409B', 0.04) }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Experience</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Pricing</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Slots</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }} align="center">
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b' }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {experiences.map((exp) => {
                  const id = exp.id || exp._id;
                  const title = exp.title || exp.name || 'Untitled';
                  const category = exp.category || 'General';
                  const location = exp.location || exp.venue || '';
                  const city = exp.city || '';
                  const duration = exp.duration || '—';
                  const price = exp.pricing ?? exp.price ?? 0;
                  const discount = exp.discountPrice ?? exp.offerPrice;
                  const slots = exp.slots ?? exp.capacity ?? '—';
                  const active = exp.active ?? exp.isActive ?? true;
                  const img = exp.imageUrl || exp.image || exp.banner || '';

                  return (
                    <TableRow key={id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      {/* Experience image + title */}
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar
                            variant="rounded"
                            src={img}
                            sx={{
                              width: 52,
                              height: 52,
                              borderRadius: '12px',
                              bgcolor: alpha('#11409B', 0.1),
                              border: '1px solid rgba(0,0,0,0.06)',
                            }}
                          >
                            {title.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                              {title}
                            </Typography>
                            <Chip
                              label={category}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                mt: 0.5,
                                bgcolor: alpha('#11409B', 0.08),
                                color: '#11409B',
                              }}
                            />
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Location */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                          {city || location || '—'}
                        </Typography>
                        {city && location && (
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            {location}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Duration */}
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                          {duration}
                        </Typography>
                      </TableCell>

                      {/* Pricing */}
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="baseline">
                          <Typography variant="body2" sx={{ fontWeight: 800, color: '#16a34a' }}>
                            ₹{Number(discount || price).toLocaleString('en-IN')}
                          </Typography>
                          {discount && discount < price && (
                            <Typography variant="caption" sx={{ textDecoration: 'line-through', color: '#94a3b8' }}>
                              ₹{Number(price).toLocaleString('en-IN')}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      {/* Slots */}
                      <TableCell>
                        <Chip
                          icon={<PeopleAltOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                          label={`${slots} slots`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            bgcolor: alpha('#7c3aed', 0.08),
                            color: '#7c3aed',
                          }}
                        />
                      </TableCell>

                      {/* Active toggle */}
                      <TableCell align="center">
                        <Tooltip title={active ? 'Click to Deactivate' : 'Click to Activate'}>
                          <Switch
                            checked={active}
                            onChange={() => handleToggleActive(exp)}
                            color="primary"
                            size="small"
                          />
                        </Tooltip>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedForDetails(exp);
                                setDetailsModalOpen(true);
                              }}
                              sx={{
                                color: '#2563eb',
                                '&:hover': { bgcolor: alpha('#2563eb', 0.1) },
                              }}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Edit Experience">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedForEdit(exp);
                                setFormModalOpen(true);
                              }}
                              sx={{
                                color: '#11409B',
                                '&:hover': { bgcolor: alpha('#11409B', 0.1) },
                              }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Experience">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedForDelete(exp);
                                setDeleteModalOpen(true);
                              }}
                              sx={{
                                color: '#dc2626',
                                '&:hover': { bgcolor: alpha('#dc2626', 0.1) },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </PanelCard>
      ) : (
        /* Catalog Card Grid View (Public Catalog Browsing Preview) */
        <Grid container spacing={3}>
          {experiences.map((exp) => {
            const id = exp.id || exp._id;
            const title = exp.title || exp.name || 'Untitled';
            const category = exp.category || 'Curated';
            const location = exp.location || exp.venue || '';
            const city = exp.city || '';
            const duration = exp.duration || 'Flexible';
            const price = exp.pricing ?? exp.price ?? 0;
            const discount = exp.discountPrice ?? exp.offerPrice;
            const slots = exp.slots ?? exp.capacity ?? '—';
            const active = exp.active ?? exp.isActive ?? true;
            const img = exp.imageUrl || exp.image || exp.banner || 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800';

            return (
              <Grid item xs={12} sm={6} md={4} key={id}>
                <PanelCard
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 36px rgba(15, 23, 42, 0.12)',
                    },
                  }}
                >
                  {/* Card Media */}
                  <Box sx={{ position: 'relative', height: 190, overflow: 'hidden', bgcolor: '#0f172a' }}>
                    <Box
                      component="img"
                      src={img}
                      alt={title}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'brightness(0.9)',
                      }}
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800';
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        right: 12,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Chip
                        label={category}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(0,0,0,0.65)',
                          color: '#fff',
                          fontWeight: 700,
                          backdropFilter: 'blur(6px)',
                        }}
                      />
                      <Chip
                        label={active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: active ? alpha('#16a34a', 0.9) : alpha('#dc2626', 0.9),
                          color: '#fff',
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Card Content */}
                  <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        fontSize: '1.05rem',
                        lineHeight: 1.3,
                        color: '#0f172a',
                        mb: 1,
                      }}
                    >
                      {title}
                    </Typography>

                    <Stack spacing={0.8} sx={{ color: '#64748b', mb: 2 }}>
                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <LocationOnOutlinedIcon sx={{ fontSize: 16, color: '#2563eb' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {city ? `${location ? location + ', ' : ''}${city}` : location || 'Flexible'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <AccessTimeOutlinedIcon sx={{ fontSize: 16, color: '#0891b2' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {duration}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <PeopleAltOutlinedIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {slots} slots available
                        </Typography>
                      </Stack>
                    </Stack>

                    <Box sx={{ mt: 'auto', pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, display: 'block' }}>
                            STARTING FROM
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="baseline">
                            <Typography variant="body1" sx={{ fontWeight: 800, color: '#16a34a' }}>
                              ₹{Number(discount || price).toLocaleString('en-IN')}
                            </Typography>
                            {discount && discount < price && (
                              <Typography variant="caption" sx={{ textDecoration: 'line-through', color: '#94a3b8' }}>
                                ₹{Number(price).toLocaleString('en-IN')}
                              </Typography>
                            )}
                          </Stack>
                        </Box>

                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedForDetails(exp);
                              setDetailsModalOpen(true);
                            }}
                            sx={{ color: '#2563eb', bgcolor: alpha('#2563eb', 0.08) }}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedForEdit(exp);
                              setFormModalOpen(true);
                            }}
                            sx={{ color: '#11409B', bgcolor: alpha('#11409B', 0.08) }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Box>
                  </Box>
                </PanelCard>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Pagination Bar */}
      {totalCount > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 3, px: 1 }}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Showing <strong>{experiences.length}</strong> of <strong>{totalCount}</strong> experiences
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" sx={{ color: '#64748b', mr: 1 }}>
              Per page:
            </Typography>
            <Select
              size="small"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              sx={{ height: 32, borderRadius: '8px', ...fieldSx }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>

            <IconButton
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              sx={{ borderRadius: '8px', border: '1px solid rgba(0,0,0,0.1)' }}
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </IconButton>

            <Typography variant="body2" sx={{ fontWeight: 700, px: 1 }}>
              {page + 1} / {totalPages}
            </Typography>

            <IconButton
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              sx={{ borderRadius: '8px', border: '1px solid rgba(0,0,0,0.1)' }}
            >
              <KeyboardArrowRightIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      )}

      {/* Add / Edit Form Modal */}
      <ExperienceFormModal
        open={formModalOpen}
        handleClose={() => {
          setFormModalOpen(false);
          setSelectedForEdit(null);
        }}
        experience={selectedForEdit}
        onSuccess={fetchExperiences}
      />

      {/* View Details Modal */}
      <ExperienceDetailsModal
        open={detailsModalOpen}
        handleClose={() => {
          setDetailsModalOpen(false);
          setSelectedForDetails(null);
        }}
        experience={selectedForDetails}
        onEdit={(exp) => {
          setSelectedForEdit(exp);
          setFormModalOpen(true);
        }}
        onDelete={(exp) => {
          setSelectedForDelete(exp);
          setDeleteModalOpen(true);
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteModalOpen}
        handleClose={() => {
          if (!deleteLoading) {
            setDeleteModalOpen(false);
            setSelectedForDelete(null);
          }
        }}
        handleConfirm={handleDeleteConfirm}
        title="Delete Experience"
        message={`Are you sure you want to remove "${selectedForDelete?.title || selectedForDelete?.name || 'this experience'}"? This action will permanently remove the experience and clean up all stored banner files.`}
        confirmLabel="Delete Experience"
        loading={deleteLoading}
      />
    </Box>
  );
};

export default ExperiencesTab;
